import {
  BankTransferTrackingService,
  TBankTransferConfirmMethod,
  TBankTransferSurface,
} from '@aca-new/app/pages/payments/shared/services/bank-transfer-tracking/bank-transfer-tracking.service';
import { ClipboardDirective } from '@aca-new/app/shared/directives/clipboard/clipboard.directive';
import { LoadingDirective } from '@aca-new/app/shared/directives/loading/loading.directive';
import { IBankInfo } from '@aca-new/app/shared/models/interfaces/bank-info.interface';
import { IBankTransferSubmitEvent } from '@aca-new/app/shared/models/interfaces/bank-transfer-submit.interface';
import { CurrencyFormatService } from '@aca-new/app/shared/services/currency-format/currency-format.service';
import { QimaButtonService } from '@aca-new/app/shared/services/exported-services/qima-button/qima-button.service';
import { QimaIconService } from '@aca-new/app/shared/services/exported-services/qima-icon/qima-icon.service';
import { QimaInputService } from '@aca-new/app/shared/services/exported-services/qima-input/qima-input.service';
import { QimaTooltipService } from '@aca-new/app/shared/services/exported-services/qima-tooltip/qima-tooltip.service';
import { AppFileDownloadService } from '@aca-new/app/shared/services/file-services/app-file-download.service';
import { AppSnackbarService } from '@aca-new/app/shared/services/modal-services/app-snackbar/app-snackbar.service';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { FormControl } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EQimaCheckboxState,
  EQimaMimeType,
  EQimaSnackbarType,
  IQimaFile,
  QimaButtonIconModule,
  QimaButtonModule,
  QimaFileModule,
  QimaIconModule,
  QimaInputCheckboxModule,
  QimaInputFileSelectModule,
  QimaInputTextModule,
  QimaTooltipDirectiveModule,
} from '@qima/ngx-qima';
import { distinctUntilChanged, map, merge } from 'rxjs';

/** Same rule as the backend: at most 64 characters of letters, digits and hyphens, surrounding spaces are trimmed. */
const TRANSACTION_ID_PATTERN = /^\s*[A-Za-z0-9-]{0,64}\s*$/;

interface IBankDetailField {
  labelKey: string;
  value: string;
}

interface IBankDetailRow {
  left: IBankDetailField;
  right: IBankDetailField;
}

@UntilDestroy()
@Component({
  selector: 'app-pay-by-transfer',
  templateUrl: './pay-by-transfer.component.html',
  styleUrls: ['./pay-by-transfer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    QimaFileModule,
    QimaIconModule,
    TranslateModule,
    QimaButtonModule,
    LoadingDirective,
    ClipboardDirective,
    QimaButtonIconModule,
    QimaInputCheckboxModule,
    QimaInputFileSelectModule,
    QimaInputTextModule,
    QimaTooltipDirectiveModule,
  ],
})
export class PayByTransferComponent implements OnInit {
  /**
   * @description
   * Bank info
   * @type {IBankInfo | undefined}
   * @default undefined
   */
  @Input('payByTransferBankInfo')
  public bankInfo: IBankInfo | undefined = undefined;

  /**
   * @description
   * Whether the component is loading
   * @type {boolean}
   * @default false
   */
  @Input('payByTransferIsLoading')
  public isLoading: boolean = false;

  /**
   * @description
   * Emits the slips, the reference confirmation and the transaction id when the customer submits.
   * @type {EventEmitter<IBankTransferSubmitEvent>}
   */
  @Output('payByTransferPaymentSubmit')
  public readonly paymentSubmit: EventEmitter<IBankTransferSubmitEvent> = new EventEmitter<IBankTransferSubmitEvent>();

  /**
   * @description
   * Backend-reserved payment reference to include in the transfer message.
   * @type {string}
   * @default ''
   */
  @Input('payByTransferPaymentReference')
  public paymentReference: string = '';

  /**
   * @description
   * Transfer amount shown in the heading and the bank-details amount field.
   * @type {number | undefined}
   * @default undefined
   */
  @Input('payByTransferAmount')
  public amount: number | undefined = undefined;

  /**
   * @description
   * Currency code paired with payByTransferAmount.
   * @type {string}
   * @default ''
   */
  @Input('payByTransferCurrency')
  public currency: string = '';

  /**
   * @description
   * Surface that opened the bank-transfer option.
   * @type {TBankTransferSurface}
   * @default 'myQIMA'
   */
  @Input('payByTransferSurface')
  public surface: TBankTransferSurface = 'myQIMA';

  /**
   * @description
   * Number of payment items covered by this transfer. The portal passes 1.
   * @type {number}
   * @default 0
   */
  @Input('payByTransferInvoiceCount')
  public invoiceCount: number = 0;

  /**
   * @description
   * System-event source id. myQIMA passes the first payment item id. The portal passes the order id.
   * @type {string}
   * @default ''
   */
  @Input('payByTransferSourceId')
  public sourceId: string = '';

  public uploadFiles: IQimaFile[] = [];
  public shouldShowTransactionId: boolean = false;
  public readonly referenceConfirmedControl: FormControl<EQimaCheckboxState> = new FormControl<EQimaCheckboxState>(EQimaCheckboxState.UNCHECKED);
  public readonly transactionIdControl: FormControl<string> = new FormControl<string>('', [Validators.pattern(TRANSACTION_ID_PATTERN)]);
  public fileSelectAccept = [EQimaMimeType.IMAGE_PNG, EQimaMimeType.IMAGE_JPG, EQimaMimeType.IMAGE_JPEG, EQimaMimeType.APPLICATION_PDF];
  public readonly qimaMimeType = EQimaMimeType;

  public constructor(
    public readonly qimaIcon: QimaIconService,
    public readonly qimaInput: QimaInputService,
    public readonly qimaButton: QimaButtonService,
    public readonly qimaTooltip: QimaTooltipService,
    private readonly _translateService: TranslateService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appSnackbarService: AppSnackbarService,
    private readonly _appFileDownloadService: AppFileDownloadService,
    private readonly _currencyFormatService: CurrencyFormatService,
    private readonly _bankTransferTrackingService: BankTransferTrackingService
  ) {}

  public ngOnInit(): void {
    merge(this.referenceConfirmedControl.valueChanges, this.transactionIdControl.valueChanges)
      .pipe(
        map((): TBankTransferConfirmMethod | undefined => this._confirmMethod()),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((confirmMethod): void => {
        this._changeDetectorRef.markForCheck();

        if (confirmMethod !== undefined) {
          this._bankTransferTrackingService.trackConfirmMethodChosen(this.surface, this.invoiceCount, confirmMethod, this.sourceId);
        }
      });
  }

  /**
   * @returns {string} Amount with currency code, or an empty string when amount is missing.
   */
  public get formattedAmount(): string {
    if (this.amount === undefined || this.amount === null) {
      return '';
    }

    return this._currencyFormatService.formatWithCode(this.amount, this.currency);
  }

  /**
   * @returns {ReadonlyArray<IBankDetailRow>} Bank detail rows in the design column order.
   */
  public get bankDetailRows(): ReadonlyArray<IBankDetailRow> {
    if (!this.bankInfo) {
      return [];
    }

    return [
      {
        left: { labelKey: 'PAY_BY_TRANSFER.ACCOUNT_NUMBER', value: this.bankInfo.accountNumber },
        right: { labelKey: 'PAY_BY_TRANSFER.SWIFT', value: this.bankInfo.swiftCode },
      },
      {
        left: { labelKey: 'PAY_BY_TRANSFER.ACCOUNT_NAME', value: this.bankInfo.accountName },
        right: { labelKey: 'PAY_BY_TRANSFER.AMOUNT', value: this.formattedAmount },
      },
      {
        left: { labelKey: 'PAY_BY_TRANSFER.BANK_NAME', value: this.bankInfo.bankName },
        right: { labelKey: 'PAY_BY_TRANSFER.BANK_ADDRESS', value: this.bankInfo.bankAddress },
      },
    ];
  }

  /**
   * @returns {boolean} Whether the client has confirmed the reference or entered a transaction ID.
   */
  public get canSubmitTransfer(): boolean {
    if (!this.hasPaymentReference) {
      return this.uploadFiles.length > 0;
    }

    const isConfirmed = this.referenceConfirmedControl.value === EQimaCheckboxState.CHECKED;
    const hasTransactionId = (this.transactionIdControl.value ?? '').trim().length > 0;

    return this.transactionIdControl.valid && (isConfirmed || hasTransactionId);
  }

  public onPdfDownloadClick(): void {
    if (this.bankInfo) {
      this._appFileDownloadService.downloadFileDirectlyByFileId(this.bankInfo.fileName, this.bankInfo.fileId);
    }
  }

  public onFileSelectFilesChange(files: IQimaFile[]): void {
    const totalSize = this.uploadFiles.concat(files).reduce((total, file): number => {
      return total + file.size;
    }, 0);

    if (totalSize > 40 * 1024 * 1024) {
      this._appSnackbarService.showSnackbar(this._translateService.instant('PAY_BY_TRANSFER.UPLOAD_FILES_OVER_LIMIT') as string, EQimaSnackbarType.WARNING, 2000);

      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (!this.fileSelectAccept.includes(files[i].type as EQimaMimeType)) {
        this._appSnackbarService.showSnackbar(
          this._translateService.instant('FILE_UPLOAD.FILE_REJECTED', { name: files[i].name ?? '' }) as string,
          EQimaSnackbarType.WARNING,
          2000
        );

        return;
      }
    }

    if (this.uploadFiles.length > 0) {
      this.uploadFiles = this.uploadFiles.concat(files);
    } else {
      this.uploadFiles = files;
    }

    this._changeDetectorRef.markForCheck();
  }

  public onRemoveClick(_file: IQimaFile, idx: number): void {
    this.uploadFiles.splice(idx, 1);
    this._changeDetectorRef.markForCheck();
  }

  public onSubmit(): void {
    this.paymentSubmit.emit({
      files: this.uploadFiles,
      referenceConfirmed: this.referenceConfirmedControl.value === EQimaCheckboxState.CHECKED,
      bankTransactionId: this.transactionIdControl.value ?? '',
    });
  }

  /**
   * @returns {boolean} Whether the payment reference panel should render.
   */
  public get hasPaymentReference(): boolean {
    return this.paymentReference.trim().length > 0;
  }

  public onBankDetailCopySuccess(): void {
    this._showCopiedSnackbar();
  }

  public onPaymentReferenceCopySuccess(): void {
    this._bankTransferTrackingService.trackReferenceCopied(this.surface, this.invoiceCount, this.sourceId);
    this._showCopiedSnackbar();
  }

  public onConfirmBoxClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (target.closest('qima-input-checkbox')) {
      return;
    }

    event.preventDefault();

    const isChecked = this.referenceConfirmedControl.value === EQimaCheckboxState.CHECKED;

    this.referenceConfirmedControl.setValue(isChecked ? EQimaCheckboxState.UNCHECKED : EQimaCheckboxState.CHECKED);
  }

  public onEnterTransactionIdClick(): void {
    this.shouldShowTransactionId = true;
    this._changeDetectorRef.markForCheck();
  }

  /**
   * @description
   * Shows the standard copy-success snackbar.
   * @returns {void} Nothing.
   */
  private _showCopiedSnackbar(): void {
    this._appSnackbarService.showSnackbar(this._translateService.instant('COMMON.COPIED') as string, EQimaSnackbarType.SUCCESS, 2000);
  }

  /**
   * @description
   * Confirm route from the checkbox and the transaction id. Empty when neither is set, or when no reference is shown.
   * @returns {TBankTransferConfirmMethod | undefined} Tick, transaction id, both, or undefined.
   */
  private _confirmMethod(): TBankTransferConfirmMethod | undefined {
    if (!this.hasPaymentReference) {
      return undefined;
    }

    const isConfirmed = this.referenceConfirmedControl.value === EQimaCheckboxState.CHECKED;
    const hasTransactionId = (this.transactionIdControl.value ?? '').trim().length > 0;

    if (isConfirmed && hasTransactionId) {
      return 'both';
    }

    if (isConfirmed) {
      return 'tick';
    }

    if (hasTransactionId) {
      return 'transaction_id';
    }

    return undefined;
  }
}
